import { createContext, useContext, useMemo, type ReactNode } from 'react';
import { DEFAULT_INTERFACE_THEME, type InterfaceThemeId } from '@kairelo/shared';
import { getInterfaceTheme } from './registry.js';
import { tokensToCssVariables, type InterfaceTheme } from './tokens.js';

/**
 * Applies an interface theme.
 *
 * Tokens become CSS custom properties on a wrapper element, so the stylesheet
 * consumes them directly and switching theme is one attribute change rather
 * than a re-render of the message tree. Components read tokens through CSS;
 * only the handful that need a value in JavaScript use `useInterfaceTheme`.
 *
 * Colour scheme follows the device. Both schemes are defined by every theme, so
 * dark mode is a property of the design rather than a later retrofit.
 */

interface InterfaceThemeValue {
  id: InterfaceThemeId;
  theme: InterfaceTheme;
}

const InterfaceThemeContext = createContext<InterfaceThemeValue>({
  id: DEFAULT_INTERFACE_THEME,
  theme: getInterfaceTheme(DEFAULT_INTERFACE_THEME),
});

export function InterfaceThemeProvider({
  themeId = DEFAULT_INTERFACE_THEME,
  /** Overrides the theme accent, so owner branding still applies (concept §10). */
  accentOverride,
  className,
  children,
}: {
  themeId?: InterfaceThemeId;
  accentOverride?: string | null;
  className?: string;
  children: ReactNode;
}) {
  const theme = getInterfaceTheme(themeId);

  const style = useMemo(() => {
    // Both schemes are emitted; a media query in the stylesheet picks one, so
    // the device preference is respected without JavaScript.
    const light = tokensToCssVariables(theme.light);
    const dark = tokensToCssVariables(theme.dark);
    const prefixed: Record<string, string> = { ...light };
    for (const [key, value] of Object.entries(dark)) prefixed[`${key}-dark`] = value;

    if (accentOverride) {
      // Branding *tints*; it does not replace. Substituting the accent straight
      // into the outgoing bubble produced a heavy block where the messenger
      // expects a pale one, which lost the look entirely. Mixing preserves the
      // theme's light/deep relationship while still carrying the owner's colour
      // to both participants, as concept §10 requires.
      prefixed['--ui-accent'] = accentOverride;
      prefixed['--ui-accent-hover'] = accentOverride;
      prefixed['--ui-quote-accent'] = accentOverride;
      prefixed['--ui-outgoing-bubble'] =
        `color-mix(in srgb, ${accentOverride} 20%, ${theme.light.incomingBubble})`;
      prefixed['--ui-outgoing-bubble-dark'] =
        `color-mix(in srgb, ${accentOverride} 62%, ${theme.dark.chatBackground})`;
      prefixed['--ui-quote-accent-dark'] = accentOverride;
    }
    return prefixed as React.CSSProperties;
  }, [theme, accentOverride]);

  const value = useMemo(() => ({ id: themeId, theme }), [themeId, theme]);

  return (
    <InterfaceThemeContext.Provider value={value}>
      <div className={className} data-interface={themeId} style={style}>
        {children}
      </div>
    </InterfaceThemeContext.Provider>
  );
}

export function useInterfaceTheme(): InterfaceThemeValue {
  return useContext(InterfaceThemeContext);
}
